import { useMutation, useQuery } from 'convex/react'
import { useState } from 'react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'
import type { OrganizationInvite } from '../types/invite.types'

/**
 * All active users in an organization, with their current role and position.
 * Returns the full list for client-side filtering and pagination.
 */
export function useOrganizationUsers(organizationId: string | undefined) {
  const result = useQuery(
    api.organization.listOrganizationUsers,
    organizationId
      ? { organizationId: organizationId as Id<'organizations'> }
      : 'skip',
  )

  return {
    data: result ?? [],
    isLoading: result === undefined,
  }
}

/**
 * All active managers in an organization.
 * Replaces `useOrganizationManagersQuery` for Convex-migrated components.
 */
export function useOrganizationManagers(organizationId: string | undefined) {
  const result = useQuery(
    api.organization.listOrganizationManagers,
    organizationId
      ? { organizationId: organizationId as Id<'organizations'> }
      : 'skip',
  )

  return {
    data: result ?? [],
    isLoading: result === undefined,
  }
}

/**
 * Direct reports for a manager in an organization.
 * Replaces `useOrganizationUsersQuery({ managerId })` for Convex-migrated components.
 */
export function useDirectReportsForManager(
  managerId: string | undefined,
  organizationId: string | undefined,
) {
  const result = useQuery(
    api.organization.listDirectReportsForManager,
    managerId && organizationId
      ? {
          managerId: managerId as Id<'users'>,
          organizationId: organizationId as Id<'organizations'>,
        }
      : 'skip',
  )

  return {
    data: result ?? [],
    isLoading: result === undefined,
  }
}

/**
 * All organization roles.
 * Replaces `useOrganizationRolesQuery` for Convex-migrated components.
 */
export function useConvexOrganizationRoles() {
  const result = useQuery(api.organization.listOrganizationRoles)

  return {
    data: result ?? [],
    isLoading: result === undefined,
  }
}

/**
 * Current roles for a specific user in an organization.
 * Replaces `useUserRolesQuery(userId)` for Convex-migrated components.
 */
export function useUserRolesInOrg(
  userId: string | undefined,
  organizationId: string | undefined,
) {
  const result = useQuery(
    api.organization.getUserRolesInOrg,
    userId && organizationId
      ? {
          userId: userId as Id<'users'>,
          organizationId: organizationId as Id<'organizations'>,
        }
      : 'skip',
  )

  return {
    data: result ?? [],
    isLoading: result === undefined,
  }
}

/**
 * All invites for an organization (pending, accepted, expired, declined).
 * Replaces `useOrganizationInvitesQuery` for Convex-migrated components.
 * Convex provides live updates — no manual invalidation needed.
 */
export function useOrganizationInvites(organizationId: string | undefined) {
  const result = useQuery(
    api.invites.listOrganizationInvites,
    organizationId
      ? { organizationId: organizationId as Id<'organizations'> }
      : 'skip',
  )

  return {
    data: (result ?? []) as Array<OrganizationInvite>,
    isLoading: result === undefined,
  }
}

/**
 * Resend an invite email and refresh the 7-day expiry window.
 * Returns an `{ isPending, mutateAsync }` shape so consumers can drive button
 * disabled state without depending on React Query.
 */
export function useResendInvite() {
  const fn = useMutation(api.invites.resendInvite)
  const [isPending, setPending] = useState(false)

  const mutateAsync = async (
    inviteId: string,
  ): Promise<{ success: boolean }> => {
    setPending(true)
    try {
      return await fn({
        inviteId: inviteId as Id<'invites'>,
        siteUrl: window.location.origin,
      })
    } finally {
      setPending(false)
    }
  }

  return { isPending, mutateAsync }
}

/**
 * Cancel (hard-delete) a pending or expired invite.
 */
export function useCancelInvite() {
  const fn = useMutation(api.invites.cancelInvite)
  const [isPending, setPending] = useState(false)

  const mutateAsync = async (
    inviteId: string,
  ): Promise<{ success: boolean }> => {
    setPending(true)
    try {
      return await fn({ inviteId: inviteId as Id<'invites'> })
    } finally {
      setPending(false)
    }
  }

  return { isPending, mutateAsync }
}

/**
 * Returns the owner of an organization.
 */
export function useOrganizationOwner(organizationId: string | undefined) {
  const result = useQuery(
    api.organization.getOrganizationOwner,
    organizationId
      ? { organizationId: organizationId as Id<'organizations'> }
      : 'skip',
  )

  return {
    data: result ?? null,
    isLoading: result === undefined,
  }
}

/**
 * Transfer ownership of an organization to another active member.
 * Only the current owner can call this.
 */
export function useTransferOwnership() {
  const fn = useMutation(api.organization.transferOwnership)
  const [isPending, setPending] = useState(false)

  const mutateAsync = async (
    organizationId: string,
    newOwnerId: string,
  ): Promise<void> => {
    setPending(true)
    try {
      await fn({
        organizationId: organizationId as Id<'organizations'>,
        newOwnerId: newOwnerId as Id<'users'>,
      })
    } finally {
      setPending(false)
    }
  }

  return { isPending, mutateAsync }
}
